/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { theme } from "styles/theme";
import Icon from "uis/Icon";
import { useEffect, useState } from "react";
import InputField from "uis/InputField";
import { TDevice, updateDeviceName } from "apis/visitors/device";
import { ColorTag } from "../components/color-tag";
import { OnOffTag } from "../components/on-off-tag";
import DeviceConfigModal from "./device-config-modal";

const Container = styled.div`
    padding: 0 40px;
    display: flex;
    flex-direction: column;
    height: 80%;
    overflow: auto;
`;

const Label = styled.p`
    color: ${theme.neutral500};
    font-size: 17px;
    font-weight: 600;
    padding: 35px 0 20px 0;
`;

const DeviceInfo = ({
    device,
    onChange,
    onChangeTab,
}: {
    device: TDevice;
    onChange?: () => void;
    onChangeTab?: (value: string) => void;
}) => {
    return (
        <Container>
            <Label>기기 정보</Label>
            <DeviceName
                name={device.deviceName}
                onChange={async (name) => {
                    const res = await updateDeviceName({ ...device, deviceName: name });
                    if (res) {
                        onChange?.();
                    }
                }}
            />
            <Label>기능 설정</Label>
            <FunctionSetting device={device} onChangeTab={onChangeTab} />
            <Label>공통</Label>
            <GetAnotherDeviceSetting device={device} onChange={onChange} />
        </Container>
    );
};

export default DeviceInfo;

const DeviceNameContainer = styled.div`
    display: flex;
    padding: 25px 30px;
    color: ${theme.neutral900};
    border-top: 1px solid ${theme.neutral50};
    border-bottom: 1px solid ${theme.neutral50};
    align-items: center;
`;

const DeviceName = ({ name, onChange }: { name: string; onChange?: (value: string) => void }) => {
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [editName, setEditName] = useState<string>();

    return (
        <DeviceNameContainer>
            <p
                css={css`
                    font-weight: 600;
                `}
            >
                기기명
            </p>
            {!isEdit && (
                <p
                    css={css`
                        font-weight: 500;
                        display: flex;
                        gap: 8px;
                        align-items: center;
                        margin-left: auto;
                        cursor: pointer;
                    `}
                >
                    {name}
                    <Icon
                        name={"pen"}
                        width={24}
                        height={24}
                        color={theme.neutral400}
                        hoverColor={theme.neutral500}
                        onClick={() => setIsEdit(true)}
                    />
                </p>
            )}
            {isEdit && (
                <>
                    <InputField
                        placeholder={name}
                        onChange={(value) => setEditName(value)}
                        styles={{
                            container: css`
                                width: 300px;
                                margin-left: auto;
                                margin-right: 12px;
                            `,
                        }}
                    />
                    <SaveNameBtn
                        onClick={() => {
                            if (editName) {
                                onChange?.(editName);
                                setIsEdit(false);
                            } else {
                                alert("기기명을 입력해주세요.");
                            }
                        }}
                    />
                </>
            )}
        </DeviceNameContainer>
    );
};

const SaveNameBtn = ({ onClick }: { onClick: () => void }) => {
    const [isHover, setIsHover] = useState<boolean>(false);
    return (
        <p
            onClick={onClick}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            css={css`
                display: flex;
                color: ${isHover ? theme.neutral500 : theme.neutral400};
                align-content: center;
                cursor: pointer;
                font-weight: 500;
                font-size: 16px;
                gap: 4px;
            `}
        >
            저장 <Icon name="check" color={isHover ? theme.neutral500 : theme.neutral400} width={20} height={20} />
        </p>
    );
};

const FunctionContainer = styled.div`
    background-color: #f9fbff;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
`;

const FunctionRow = styled.div`
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.neutral900};
    padding: 16px 30px;
    border-bottom: 2px solid ${theme.neutral50};
    justify-content: space-between;
    border-radius: 20px;
    &:hover {
        background-color: ${theme.neutral50};
    }

    cursor: pointer;
`;

const FunctionSetting = ({ device, onChangeTab }: { device: TDevice; onChangeTab?: (value: string) => void }) => {
    return (
        <FunctionContainer>
            <FunctionRow onClick={() => onChangeTab?.("홈 화면 구성")}>
                <p>테마 색상</p>
                <ColorTag color={device.visitorsSettingTheme.primary} />
            </FunctionRow>
            <FunctionRow onClick={() => onChangeTab?.("홈 화면 구성")}>
                <p>인삿말 설정</p>
                <OnOffTag state={device.isInfoMessageEnabled} />
            </FunctionRow>
            <FunctionRow onClick={() => onChangeTab?.("초인종")}>
                <p>초인종 활성화</p>
                <OnOffTag state={device.isDoorBellEnabled} />
            </FunctionRow>
            <FunctionRow
                onClick={() => onChangeTab?.("당일 방문")}
                css={css`
                    border: none;
                `}
            >
                <p>당일 방문 활성화</p>
                <OnOffTag state={device.isWalkInVisitEnabled} />
            </FunctionRow>
        </FunctionContainer>
    );
};

const AnotherDeviceContainer = styled.div`
    border-radius: 15px;
    background-color: ${theme.neutral50};
    padding: 12px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 15px;
    font-weight: 500;
`;

const GetAnotherDeviceSetting = ({ device, onChange = () => {} }: { device: TDevice; onChange?: () => void }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    return (
        <AnotherDeviceContainer>
            <p>다른 기기 설정 불러오기</p>
            <GetIconButton onClick={() => setIsOpen(true)} />
            <DeviceConfigModal modalIsOpen={isOpen} closeModal={() => setIsOpen(false)} onChange={onChange} device={device} />
        </AnotherDeviceContainer>
    );
};

const BtnContainer = styled.div<{ isHover: boolean }>`
    display: flex;
    padding: 10px;
    border-radius: 8px;
    background-color: ${(props) => (props.isHover ? theme.primary500 : theme.primary50)};
`;

const GetIconButton = ({ onClick }: { onClick: () => void }) => {
    const [isHover, setIsHover] = useState<boolean>(false);

    return (
        <BtnContainer onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)} isHover={isHover} onClick={onClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                    d="M6 21V19L7 18H4C3.45 18 2.97917 17.8042 2.5875 17.4125C2.19583 17.0208 2 16.55 2 16V5C2 4.45 2.19583 3.97917 2.5875 3.5875C2.97917 3.19583 3.45 3 4 3H12V5H4V16H20V13H22V16C22 16.55 21.8042 17.0208 21.4125 17.4125C21.0208 17.8042 20.55 18 20 18H17L18 19V21H6ZM15 15L10 10L11.4 8.6L14 11.175V3H16V11.175L18.6 8.6L20 10L15 15Z"
                    fill={isHover ? "white" : theme.primary500}
                />
            </svg>
        </BtnContainer>
    );
};
